// Ported from dev-scrips-pm-react src/billing/components/edit-service-discount-dialog.tsx (PROD-3312 appointment
// Billing tab, step 4). Edit / remove the discount on one invoice service line:
//   PUT /api/Billing/Invoice/UpdateServiceDiscount?invoiceId={id}  body { invoiceDetailsId, discountAmount, discountReason }
// `invoiceDetailsId` is the LINE id (`service.id`), never the service master id. `discountAmount: null` removes.
// Differences from pm-react, none of them behavioural:
//   - the fields seed from the service in `useState` initialisers instead of an open-gated effect: the page mounts
//     this dialog per target (`{discountTarget && <EditServiceDiscountDialog open … />}`), so a fresh mount is the
//     open. pm-react's effect also re-seeded (and wiped the typed amount) whenever a background refetch changed the
//     service's discount; the initialiser does not.
//   - the reason list is the shared `DISCOUNT_REASONS` (byte-identical to pm-react's local copy).
//   - the reason trigger gets an aria-label (pm-react's <label> is not associated with it).
//   - `[&_input]:tabular-nums` (arbitrary variant, banned) becomes `tabular-nums` on the field.
import { useState } from 'react';
import { TextField } from '@scripsteam/scrips-signal-ds';
import {
  BridgeButton as Button,
  BridgeSelect as Select,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/billing/bridge';
import { DISCOUNT_REASONS } from '@/billing/lib/discount-reasons';
import { useUpdateServiceDiscount } from '@/billing/services/use-invoice-actions';
import { formatMoney } from '@/shared/lib/money';
import type { InvoiceService } from '@/shared/types/billing';

interface EditServiceDiscountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoiceId: string;
  service: InvoiceService;
}

export function EditServiceDiscountDialog({ open, onOpenChange, invoiceId, service }: EditServiceDiscountDialogProps) {
  const updateDiscount = useUpdateServiceDiscount();
  const detailsId = service.id ?? '';
  // Discount is capped at the service's gross. The wire ships `grossAmount`; `fee` is usually absent on invoice rows
  // (the "fee 0" bug). Fall back through gross -> fee -> net so the cap is never wrongly 0.
  const fee = service.grossAmount ?? service.fee ?? service.netAmount ?? 0;
  const current = service.discountAmount ?? service.discount;

  const [amount, setAmount] = useState(() => (current != null && current > 0 ? String(current) : ''));
  // Reopen shows the previously-saved reason (Flutter `edit_service_popup` pre-selects the stored `discountReason`).
  const [reason, setReason] = useState(() => service.discountReason ?? '');

  const parsed = Number.parseFloat(amount);
  // Apply requires a POSITIVE amount within the fee AND a reason (both required, Andrew 2026-06-04). Remove needs neither.
  const validAmount = !Number.isNaN(parsed) && parsed > 0 && parsed <= fee + 0.01;
  const hasReason = reason.trim() !== '';
  const canApply = validAmount && hasReason;

  const apply = async (clear = false) => {
    if (!detailsId) return;
    if (!clear && !canApply) return;
    try {
      await updateDiscount.mutateAsync({
        invoiceId,
        invoiceDetailsId: detailsId,
        discountAmount: clear ? null : parsed,
        discountReason: clear ? undefined : reason.trim(),
      });
      onOpenChange(false);
    } catch {
      /* Global toast surfaces failure. */
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Adjust service discount</DialogTitle>
          <DialogDescription>
            {service.procedureCode?.displayName ?? 'Service'} · Fee{' '}
            <span className="font-medium text-text-primary">{formatMoney(fee)}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 px-1">
          <TextField
            label="Discount amount"
            type="text"
            size="compact"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            error={
              amount && !validAmount ? `Discount must be > 0 and ≤ ${formatMoney(fee)} (service fee).` : undefined
            }
            className="tabular-nums"
          />

          <div className="space-y-2">
            <label className="text-xs font-medium text-text-muted">Reason</label>
            <Select value={reason} onValueChange={(v) => setReason(v ?? '')}>
              <SelectTrigger className="w-full" aria-label="Reason">
                <SelectValue placeholder="Select a reason" />
              </SelectTrigger>
              <SelectContent>
                {DISCOUNT_REASONS.map((r) => (
                  <SelectItem key={r} value={r}>
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {validAmount && !hasReason && <p className="text-xs text-status-error">Select a discount reason.</p>}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={updateDiscount.isPending}>
            Cancel
          </Button>
          {(service.discountAmount ?? service.discount ?? 0) > 0 && (
            <Button
              variant="ghost"
              onClick={() => apply(true)}
              disabled={updateDiscount.isPending}
              className="text-status-error hover:bg-red-100"
            >
              Remove discount
            </Button>
          )}
          <Button onClick={() => apply(false)} disabled={!canApply || updateDiscount.isPending}>
            {updateDiscount.isPending ? 'Applying…' : 'Apply'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
